import { useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { TaskItemStatus } from "src/constants";
import {
  EditTaskItemStatusDto,
  FetchError,
  GetUserTaskItemsDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  TaskItemDetailDto,
  useAccessToken,
  useTaskItemMutation,
} from "src/http";

type Variables = {
  taskItemGuid: string;
  statusId: TaskItemStatus;
  teamSlug?: string;
  ownerUserGuids: string[];
};

const useEditTaskItemStatusRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemStatusRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemStatusDto = {
      statusId: variables.statusId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/status`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemStatusRequest };
};

const useEditTaskItemStatus = () => {
  const { editTaskItemStatusRequest } = useEditTaskItemStatusRequest();
  const queryClient = useQueryClient();

  return useTaskItemMutation({
    mutationFn: editTaskItemStatusRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;
      let cachedUserData: { [userGuid: string]: GetUserTaskItemsDto | undefined } = {};

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        () => ({
          currentStatus: variables.statusId,
        })
      );

      if (variables.ownerUserGuids) {
        variables.ownerUserGuids.forEach(async (userGuid) => {
          cachedUserData[userGuid] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
            queryClient,
            queryKeys.taskItems.getUserTaskItems(userGuid),
            (cachedData) => ({
              taskItems: cachedData.taskItems.map((taskItem) => {
                if (taskItem.guid === variables.taskItemGuid) {
                  return { ...taskItem, currentStatus: variables.statusId };
                } else {
                  return taskItem;
                }
              }),
            })
          );
        });
      }

      return {
        cachedDetailData,
        cachedUserData,
      };
    },
  });
};

export { useEditTaskItemStatus };
